/* eslint-disable react/require-default-props */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react'
import { useEffect } from 'react'
import { useState } from 'react'
import idDirectory from './idAttributes'
import CB from 'lib'
import { Merchant } from 'lib/Merchants'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { useActiveMerchant } from 'components'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import SearchableSelect from 'components/SearchableSelect'
import { IconButton } from '@mui/material'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'

/**
 * @desc - MerchantSwitcher provides us with a wrapped MUI Autocomplete component that takes in an options list and handleChange function. It then provides us with a selectable and searchable auto-complete input made from those options.
 * @param defaultValue - optional default value for the input
 * @param merchantOptions - list of options to render the autocomlete list with
 * @param handleValueSelect - onChange function to extract the input value selected
 * @param className?
 * @param id?
 * @param label - autocomplete input label
 * @param loading
 * @param error
 *
 */

export interface MerchantSwitcherProps {
    testId?: string
    className?: string
}

// Extend Merchant type to satisfy the Option type requirements
type MerchantOption = Merchant & Record<string, unknown>

const MerchantSwitcher = ({
    testId,
    className = '',
}: MerchantSwitcherProps) => {
    const { themeVariantObj } = useThemeStyling()
    const [merchants, setMerchants] = useState<MerchantOption[] | null>(null)
    const { user } = useAuthedUser()
    const {
        name,
        setActiveMerchant,
        isRootMerchant,
        userDefaultMerchant,
        id: selectedId,
    } = useActiveMerchant()
    const { MERCHANT_LIST } = useFeatureToggle('MERCHANT_SWITCHER')
    const [merchantSearchLoading, setMerchantSearchLoading] = useState<boolean>(
        false
    )

    const rootMerchantLabel = useMemo(
        () =>
            [8].includes(themeVariantObj.id)
                ? 'All Brands'
                : 'ALL MERCHANTS',
        [themeVariantObj.id]
    )

    const value = React.useMemo(() => {
        if (isRootMerchant) {
            return `${name} - ${rootMerchantLabel}`
        }
        return name
    }, [name, isRootMerchant, rootMerchantLabel])

    useEffect(() => {
        const merchantId = user?.merchant?.id.toString()
        if (merchantId) {
            setMerchantSearchLoading(true)
            CB.merchants
                .list(merchantId, {})
                .then((res) => {
                    const processedMerchants = res.data.map((merchant) => {
                        const updatedMerchant = { ...merchant }
                        if (!merchant.parent_id) {
                            updatedMerchant.business_name = `${merchant.business_name} - ${rootMerchantLabel}`
                        } else if (MERCHANT_LIST.enabled_merchant_alias) {
                            updatedMerchant.business_name = `${merchant.business_name} - ${merchant.alias}`
                        }
                        return updatedMerchant
                    })

                    // Sort to put root merchant (without parent_id) first
                    const sortedMerchants = processedMerchants.sort((a, b) => {
                        if (!a.parent_id && b.parent_id) return -1
                        if (a.parent_id && !b.parent_id) return 1
                        return 0
                    })

                    setMerchants(sortedMerchants)
                })
                .finally(() => setMerchantSearchLoading(false))
        }
    }, [user, MERCHANT_LIST.enabled_merchant_alias, rootMerchantLabel])

    const handleChange = (selectedValue: Partial<Merchant> | null) => {
        if (selectedValue && selectedValue.id && selectedValue.business_name) {
            setActiveMerchant({
                id: selectedValue.id.toString(),
                name: selectedValue.business_name.replace(
                    ` - ${rootMerchantLabel}`,
                    ''
                ),
            })
        } else {
            setActiveMerchant({
                id: user?.merchant?.id.toString() ?? '1',
                name: user?.merchant?.business_name ?? '',
            })
        }
    }

    return (
        <div
            id={`${idDirectory.divRoot}-${testId}`}
            className={`emp-merchantSwitcher-root ${className}`}
        >
            <div style={{ flex: '1 1 auto' }}>
                <SearchableSelect
                    value={value}
                    onValueChange={handleChange}
                    options={merchants ?? []}
                    accessor="business_name"
                    testId={`${testId}-businessName`}
                    loadingValues={merchantSearchLoading}
                    hideSearch
                    isPaginate={false}
                />
            </div>
            {userDefaultMerchant !== selectedId && userDefaultMerchant && (
                <div style={{ padding: '2px 0 2px 8px' }}>
                    <IconButton
                        size="small"
                        onClick={() =>
                            setActiveMerchant({
                                name: '',
                                id: user?.merchant?.id.toString() ?? '1',
                            })
                        }
                    >
                        <Icon icon={faTimes} />
                    </IconButton>
                </div>
            )}
        </div>
    )
}

export default MerchantSwitcher
